import { OrderV2Dto, SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { Icon } from "@sellar-io/components";
import dayjs from "dayjs";
import { useBasketContext, useSellarUser } from "../../../../../hooks";
import { useOpenDistributionOrderList } from "../../../../../hooks/api/v2/order/v2/useOpenDistributionOrderList";
import { useDistributionDeliveryEstimate } from "../../../../../hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";
import { pluralize } from "@sellar-io/common";
import { useState } from "react";
import { DistributionEducationDialog } from "../../../../discovery/v2/distribution/DistributionEducationDialog";
import { DistributionEducationDialogPage } from "../../../../discovery/v2/distribution/DistributionEducationDialog.shared";
import { useStorefrontBuyerPostcode } from "../lib/useStorefrontBuyerPostcode";
import {
  BuyersChoiceFulfillmentType,
  useStorefrontBuyerChoiceDeliveryOption,
} from "../StorefrontBuyerChoiceDeliveryOptionDialog/useStorefrontBuyerChoiceDeliveryOptions";
import { useStockListContext } from "../../../useStockListContext";
import { CheckoutSelectedFulfillmentType } from "../../..";
import { useCanDisplayStorefrontConsolidatedBasketBanner } from "../StorefrontConsolidatedBasketBanner/useCanDisplayStorefrontConsolidatedBasketBanner";

type StorefrontDistributionBannerProps = {
  supplier: SupplierGetResponseType;
};

export const StorefrontDistributionBanner = ({
  supplier,
}: StorefrontDistributionBannerProps) => {
  const { user } = useSellarUser();
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  const { buyersChoiceFulfillmentType } =
    useStorefrontBuyerChoiceDeliveryOption({
      supplierId: supplier.id,
    });
  const { checkoutSelectedFulfillmentType } = useStockListContext();
  const [{ order }] = useBasketContext();
  const canDisplayConsolidatedBasketBanner =
    useCanDisplayStorefrontConsolidatedBasketBanner(supplier);

  const { nextDeliveryDate: nextDistributionDeliveryDate } =
    useDistributionDeliveryEstimate({
      supplierId: supplier.id,
      buyerPostcode: buyerPostcode,
      productIds: order?.OrderItems?.map((item) => item.ProductId) ?? [],
    });

  const openDistributionOrderListQuery = useOpenDistributionOrderList(
    {
      query: nextDistributionDeliveryDate
        ? [
            {
              type: "deliveryPlannedAt",
              modifier: "EQUALS",
              value: nextDistributionDeliveryDate,
            } as const,
          ]
        : [],
      limit: 20,
    },
    {
      enabled: Boolean(nextDistributionDeliveryDate) && !!user,
    },
  );

  const canDisplayStorefrontDistributionBanner =
    !canDisplayConsolidatedBasketBanner &&
    computeIsStorefrontDistributionBannerVisible({
      buyersChoiceFulfillmentType: buyersChoiceFulfillmentType ?? null,
      checkoutSelectedFulfillmentType,
      nextDistributionDeliveryDate,
    });

  if (!canDisplayStorefrontDistributionBanner) {
    return null;
  }

  const openDistributionOrders =
    openDistributionOrderListQuery.data?.pages.flat() ?? [];

  if (openDistributionOrders.length === 0) {
    return (
      <NoOpenDistributionOrderBanner
        buyerPostcode={buyerPostcode}
        supplierId={supplier.id}
      />
    );
  }

  return (
    nextDistributionDeliveryDate && (
      <OpenDistributionOrderBanner
        openDistributionOrders={openDistributionOrders}
        nextDistributionDeliveryDate={nextDistributionDeliveryDate}
        supplierName={supplier.Business.name || ""}
        supplierId={supplier.id}
        buyerPostcode={buyerPostcode}
      />
    )
  );
};

type NoOpenDistributionOrderBannerProps = {
  buyerPostcode: string;
  supplierId: number | null;
};
const NoOpenDistributionOrderBanner = ({
  buyerPostcode,
  supplierId,
}: NoOpenDistributionOrderBannerProps) => {
  const [openDistributionEducationDialog, setOpenDistributionEducationDialog] =
    useState(false);

  return (
    <>
      <div className="flex flex-row gap-4 border-y border-y-[#D1A7E9] bg-[#E8D3F454] p-4 xl:flex-col">
        <div className="flex xl:hidden">
          <Icon
            icon="SELLAR_DISTRIBUTION_TRUCK"
            size={1}
            viewBox="0 0 16 16"
            className="text-[#8C23C7]"
          />
        </div>
        <div className="flex flex-col items-start justify-between gap-4 text-zinc-800 xl:flex-row xl:items-center">
          <div className="flex items-center gap-4">
            <div className="hidden xl:flex">
              <Icon
                icon="SELLAR_DISTRIBUTION_TRUCK"
                size={1}
                viewBox="0 0 16 16"
                className="text-[#8C23C7]"
              />
            </div>
            <div className="flex flex-col items-start gap-2 xl:flex-row xl:items-center">
              <span className="font-semibold">
                Free expert delivery from Sellar
              </span>
              <span className="hidden text-sm text-zinc-600 xl:flex">•</span>
              <span className="text-sm text-zinc-600">
                No order minimums, with orders from multiple suppliers delivered
                together - at a time that suits you.
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpenDistributionEducationDialog(true)}
            className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-[#8C23C7]"
          >
            See how it works
            <Icon icon="CHEVRON_RIGHT" />
          </button>
        </div>
      </div>
      <DistributionEducationDialog
        active={openDistributionEducationDialog}
        onDismiss={() => {
          setOpenDistributionEducationDialog(false);
        }}
        initialPage={DistributionEducationDialogPage.EXPERT_DRAY_DELIVERY}
        buyerPostcode={buyerPostcode}
        supplierId={supplierId}
      />
    </>
  );
};

type OpenDistributionOrderBannerProps = {
  openDistributionOrders: OrderV2Dto.OpenDistributionOrderListResponseType;
  nextDistributionDeliveryDate: Date;
  supplierName: string;
  supplierId: number | null;
  buyerPostcode: string;
};

const OpenDistributionOrderBanner = ({
  openDistributionOrders,
  nextDistributionDeliveryDate,
  supplierName,
  buyerPostcode,
}: OpenDistributionOrderBannerProps) => {
  const [openDistributionEducationDialog, setOpenDistributionEducationDialog] =
    useState(false);
  const formattedNextDistributionDeliveryDay = dayjs(
    nextDistributionDeliveryDate,
  ).format("dddd");

  const nbOpenDistributionOrders = openDistributionOrders.length;
  return (
    <>
      <div className="flex flex-row gap-4 border-y border-y-[#D1A7E9] bg-[#E8D3F454] p-4 xl:flex-col">
        <div className="flex h-9 min-h-9 w-9 min-w-9 items-center justify-center rounded-lg border border-[#BA7BDD] bg-[#E8D3F4] text-sm font-semibold text-[#8C23C7] xl:hidden">
          {nbOpenDistributionOrders}x
        </div>
        <div className="flex flex-col items-start justify-between gap-4 text-zinc-800 xl:flex-row xl:items-center">
          <div className="flex items-center gap-4">
            <div className="hidden h-9 min-h-9 w-9 min-w-9 items-center justify-center rounded-lg border border-[#BA7BDD] bg-[#E8D3F4] text-sm font-semibold text-[#8C23C7] xl:flex">
              {nbOpenDistributionOrders}x
            </div>
            <div className="flex flex-col items-start gap-2 xl:flex-row xl:items-center">
              <span className="font-semibold">
                Add to your {formattedNextDistributionDeliveryDay} delivery
              </span>
              <span className="hidden text-sm text-zinc-600 xl:flex">•</span>
              <span className="text-sm text-zinc-600">
                Your {supplierName} order can arrive with your existing{" "}
                {pluralize("order", nbOpenDistributionOrders)} Delivered by
                Sellar
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpenDistributionEducationDialog(true)}
            className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-[#8C23C7]"
          >
            Learn more
            <Icon icon="CHEVRON_RIGHT" />
          </button>
        </div>
      </div>
      <DistributionEducationDialog
        active={openDistributionEducationDialog}
        onDismiss={() => {
          setOpenDistributionEducationDialog(false);
        }}
        initialPage={DistributionEducationDialogPage.EXPERT_DRAY_DELIVERY}
        buyerPostcode={buyerPostcode}
        supplierId={null}
      />
    </>
  );
};

const computeIsStorefrontDistributionBannerVisible = ({
  buyersChoiceFulfillmentType,
  checkoutSelectedFulfillmentType,
  nextDistributionDeliveryDate,
}: {
  buyersChoiceFulfillmentType: BuyersChoiceFulfillmentType | null;
  checkoutSelectedFulfillmentType: CheckoutSelectedFulfillmentType | null;
  nextDistributionDeliveryDate: Date | null;
}) => {
  if (!nextDistributionDeliveryDate) {
    return false;
  }

  if (
    buyersChoiceFulfillmentType &&
    buyersChoiceFulfillmentType !== "sellarDistribution"
  ) {
    return false;
  }

  return checkoutSelectedFulfillmentType === "sellarDistribution";
};
